import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, ArrowRight, Phone, ListTodo, UserCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { DetailDrawer, CreateDialog, BADGE, LABEL, STATUSES } from "@/pages/agent/AgentWorkspace";
import { useAuth } from "@/context/AuthContext";

export default function HelmHouseholds() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "platform_admin";
  const [rows, setRows] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [scope, setScope] = useState("");
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [producers, setProducers] = useState([]);

  useEffect(() => {
    if (isOwner) api.get("/agent/producers").then(({ data }) => setProducers(data.producers || [])).catch(() => {});
  }, [isOwner]);

  const assignRep = async (householdId, producerId) => {
    try {
      await api.post(`/agent/households/${householdId}/assign`, { producer_id: producerId || null });
      await load();
    } catch (e) { /* surfaced by reload */ }
  };

  const load = async () => {
    setRows(null); setLoadErr("");
    try {
      const { data } = await api.get("/agent/households", { params: { q, status, scope } });
      setRows(data.households || []);
    } catch (e) {
      setRows([]);
      setLoadErr(e?.response?.data?.detail || "Couldn't load households. Please retry.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, scope]);

  const openDetail = async (id) => {
    setDetail({ loading: true });
    const { data } = await api.get(`/agent/households/${id}`);
    setDetail(data);
  };

  return (
    <div className="p-6 sm:p-10 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display font-bold text-3xl lg:text-4xl text-[#0B192C] tracking-tight" data-testid="households-title">My Households</h1>
        <p className="text-muted-foreground mt-1.5" data-testid="households-scope-label">
          {isOwner
            ? "Owner view — all households across the agency (not just your assigned book). Acquisition attribution and household IDs preserved."
            : "Your assigned book — acquisition attribution and household IDs preserved."}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name or state" className="pl-9 h-11 bg-white" data-testid="households-search" />
        </div>
        <Button onClick={load} variant="outline" className="h-11" data-testid="households-search-btn">Search</Button>
        <Button onClick={() => setCreateOpen(true)} className="h-11 bg-[#0B192C] text-white" data-testid="households-create-btn"><Plus className="w-4 h-4 mr-1" /> New household</Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="households-scope-filters">
        <FilterPill active={scope === ""} onClick={() => setScope("")} label={isOwner ? "All (agency)" : "My book"} testid="scope-mine" />
        <FilterPill active={scope === "unassigned"} onClick={() => setScope("unassigned")} label="Unassigned leads" testid="scope-unassigned" />
        <FilterPill active={scope === "self_enrolling"} onClick={() => setScope("self_enrolling")} label="Self-enrolling" testid="scope-self-enrolling" />
        {isOwner && <FilterPill active={scope === "test"} onClick={() => setScope("test")} label="Test & archived" testid="scope-test" />}
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="households-status-filters">
        <FilterPill active={status === ""} onClick={() => setStatus("")} label="All" testid="filter-all" />
        {STATUSES.map((s) => (
          <FilterPill key={s} active={status === s} onClick={() => setStatus(s)} label={LABEL(s)} testid={`filter-${s}`} />
        ))}
      </div>

      {!rows ? <Skeleton className="h-72 w-full rounded-xl" /> : loadErr ? (
        <div className="rounded-xl border-2 border-[#F3C9C9] bg-[#FDF3F3] p-10 text-center" data-testid="households-error">
          <p className="text-[#8A1C1C] font-semibold">{loadErr}</p>
          <Button className="mt-3" variant="outline" onClick={load} data-testid="households-retry">Retry</Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-10 text-center text-[#6B7280]" data-testid="households-empty">No households match this view.</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border-2 border-[#E5E5E0] bg-white overflow-hidden" data-testid="households-table">
            <table className="w-full text-left">
              <thead className="bg-[#F8F8F5] text-xs uppercase text-[#6B7280]">
                <tr><th className="p-3">Name</th><th className="p-3">State</th><th className="p-3">Source</th><th className="p-3">Assessment</th><th className="p-3">Rep</th><th className="p-3">Status</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.household_id} className="border-t border-[#EEE] hover:bg-[#FBFBF9]" data-testid={`household-row-${r.household_id}`}>
                    <td className="p-3 font-semibold text-[#0B192C]">
                      {r.name}
                      {r.requested_contact && <Phone className="w-3.5 h-3.5 inline ml-2 text-[#2D6A4F]" title="Requested contact" data-testid={`household-contact-flag-${r.household_id}`} />}
                      {r.open_tasks > 0 && <span className="text-[10px] font-bold ml-2 px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]" title="Open tasks"><ListTodo className="w-3 h-3 inline mr-0.5" />{r.open_tasks}</span>}
                      {r.self_enrolling && <span className="text-[10px] font-bold ml-2 px-1.5 py-0.5 rounded-full bg-[#E0ECFF] text-[#1E3A8A]" title={`Opened self-enrollment${r.self_enroll_plan ? ` — ${r.self_enroll_plan}` : ""} (not a submitted application) — follow up`} data-testid={`household-selfenroll-flag-${r.household_id}`}><ExternalLink className="w-3 h-3 inline mr-0.5" />Self-enrolling</span>}
                    </td>
                    <td className="p-3">{r.state || "—"}</td>
                    <td className="p-3 text-sm">{r.acquisition_source || "—"}</td>
                    <td className="p-3 text-sm">{r.assessment_status || "—"}</td>
                    <td className="p-3 text-sm" data-testid={`household-rep-${r.household_id}`}>
                      {isOwner ? (
                        <select value={r.assigned_producer_id || ""} onChange={(e) => assignRep(r.household_id, e.target.value)}
                          className={`text-xs border rounded px-2 py-1 bg-white ${r.assigned_producer_id ? "text-[#0B192C]" : "text-[#B45309] border-[#FCD34D] bg-[#FFFBEB]"}`}
                          data-testid={`household-assign-${r.household_id}`}>
                          <option value="">Unassigned</option>
                          {producers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      ) : (
                        r.assigned_producer_name || <span className="text-[#B45309]">Unassigned</span>
                      )}
                    </td>
                    <td className="p-3"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${BADGE[r.lifecycle] || "bg-gray-100"}`}>{LABEL(r.lifecycle)}</span></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/agent/households/${r.household_id}`)} data-testid={`household-profile-${r.household_id}`}><UserCircle className="w-3.5 h-3.5 mr-1" />Profile</Button>
                      <Button size="sm" variant="ghost" onClick={() => openDetail(r.household_id)} data-testid={`household-open-${r.household_id}`}>Open <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3" data-testid="households-cards">
            {rows.map((r) => (
              <button key={r.household_id} onClick={() => navigate(`/agent/households/${r.household_id}`)}
                className="w-full text-left rounded-xl border-2 border-[#E5E5E0] bg-white p-4" data-testid={`household-card-${r.household_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-[#0B192C]">{r.name}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${BADGE[r.lifecycle] || "bg-gray-100"}`}>{LABEL(r.lifecycle)}</span>
                </div>
                <p className="text-xs text-[#6B7280] mt-1">{r.state || "—"} · source {r.acquisition_source || "—"}</p>
                <p className="text-xs mt-1">Rep: {r.assigned_producer_name ? <span className="font-semibold text-[#0B192C]">{r.assigned_producer_name}</span> : <span className="text-[#B45309] font-semibold">Unassigned</span>}</p>
                <div className="flex gap-2 mt-2">
                  {r.requested_contact && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#E7F3EC] text-[#1F3D2F]"><Phone className="w-3 h-3 inline mr-0.5" />Contact requested</span>}
                  {r.self_enrolling && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#E0ECFF] text-[#1E3A8A]"><ExternalLink className="w-3 h-3 inline mr-0.5" />Self-enrolling</span>}
                  {r.open_tasks > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]"><ListTodo className="w-3 h-3 inline mr-0.5" />{r.open_tasks} task{r.open_tasks === 1 ? "" : "s"}</span>}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <DetailDrawer detail={detail} onClose={() => setDetail(null)} reload={load} navigate={navigate} />
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(id) => { setCreateOpen(false); load(); openDetail(id); }} />
    </div>
  );
}

function FilterPill({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={`households-${testid}`}
      className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-colors ${
        active ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-[#374151] border-[#E5E5E0] hover:border-[#D4AF37]"}`}>
      {label}
    </button>
  );
}
